import React, { useState, useRef } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Typography,
  IconButton,
  Alert,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
} from '@mui/material';
import {
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  Description as DescriptionIcon,
  AudioFile as AudioFileIcon,
  PictureAsPdf as PdfIcon,
  TableChart as TableIcon,
  Code as CodeIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../services/api';

// Allowed file types and their configurations
const ALLOWED_FILE_TYPES = {
  // Documents
  '.txt': { type: 'text/plain', icon: DescriptionIcon, label: 'Text' },
  '.md': { type: 'text/markdown', icon: DescriptionIcon, label: 'Markdown' },
  '.json': { type: 'application/json', icon: CodeIcon, label: 'JSON' },
  '.html': { type: 'text/html', icon: CodeIcon, label: 'HTML' },
  '.docx': { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', icon: DescriptionIcon, label: 'Word' },
  '.doc': { type: 'application/msword', icon: DescriptionIcon, label: 'Word' },
  '.pdf': { type: 'application/pdf', icon: PdfIcon, label: 'PDF' },

  // Spreadsheets
  '.xlsx': { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', icon: TableIcon, label: 'Excel' },
  '.xls': { type: 'application/vnd.ms-excel', icon: TableIcon, label: 'Excel' },

  // Audio
  '.mp3': { type: 'audio/mpeg', icon: AudioFileIcon, label: 'MP3' },
  '.wav': { type: 'audio/wav', icon: AudioFileIcon, label: 'WAV' },
  '.m4a': { type: 'audio/mp4', icon: AudioFileIcon, label: 'M4A' },
  '.ogg': { type: 'audio/ogg', icon: AudioFileIcon, label: 'OGG' },
  '.webm': { type: 'audio/webm', icon: AudioFileIcon, label: 'WebM' },
};

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB

// eslint-disable-next-line max-lines-per-function
const FileUploadButton = ({
  entityId,
  noteId,
  onUploadComplete,
  compact = false,
  autoTrigger = false
}) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [hasAutoTriggered, setHasAutoTriggered] = useState(false);

  const fileInputRef = useRef(null);

  // Auto-trigger file dialog if autoTrigger prop is true
  React.useEffect(() => {
    if (autoTrigger && !hasAutoTriggered && fileInputRef.current) {
      setTimeout(() => {
        fileInputRef.current?.click();
        setHasAutoTriggered(true);
      }, 300); // Small delay to ensure component is mounted
    }
  }, [autoTrigger, hasAutoTriggered]);

  // Get file icon based on extension
  const getFileIcon = (filename) => {
    const ext = '.' + filename.split('.').pop().toLowerCase();
    const config = ALLOWED_FILE_TYPES[ext];
    if (!config) return DescriptionIcon;
    return config.icon;
  };

  // Get file type label
  const getFileTypeLabel = (filename) => {
    const ext = '.' + filename.split('.').pop().toLowerCase();
    const config = ALLOWED_FILE_TYPES[ext];
    return config ? config.label : 'File';
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1024 / 1024).toFixed(1) + ' MB';
  };

  // Validate file
  const validateFile = (file) => {
    const ext = '.' + file.name.split('.').pop().toLowerCase();

    // Check file type
    if (!ALLOWED_FILE_TYPES[ext]) {
      return {
        valid: false,
        error: `File type "${ext}" is not supported. Allowed types: ${Object.keys(ALLOWED_FILE_TYPES).join(', ')}`
      };
    }

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return {
        valid: false,
        error: `File "${file.name}" is too large (${formatFileSize(file.size)}). Maximum allowed size is ${formatFileSize(MAX_FILE_SIZE)}.`
      };
    }

    return { valid: true };
  };

  // Handle file selection
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setError(null);

    // Validate each file
    const validatedFiles = [];
    const errors = [];

    files.forEach(file => {
      const validation = validateFile(file);
      if (validation.valid) {
        validatedFiles.push(file);
      } else {
        errors.push(validation.error);
      }
    });

    if (errors.length > 0) {
      setError(errors.join('\n'));
      toast.error(`${errors.length} file(s) failed validation`);
    }

    if (validatedFiles.length > 0) {
      setSelectedFiles(validatedFiles);
      setDialogOpen(true);
      toast.success(`${validatedFiles.length} file(s) selected`);
    }

    // Reset input
    event.target.value = '';
  };

  // Handle upload
  const handleUpload = async () => {
    if (selectedFiles.length === 0 || !entityId) return;

    setUploading(true);
    setProgress(0);
    setError(null);

    try {
      const uploadedFiles = [];

      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];

        // Update progress
        const currentProgress = (i / selectedFiles.length) * 100;
        setProgress(currentProgress);

        toast.info(`Uploading ${file.name}...`);

        // Upload file
        const result = await api.uploadFile(entityId, file, {
          noteId: noteId || null,
          uploadedAt: new Date().toISOString(),
        });

        uploadedFiles.push({
          ...result,
          fileName: file.name,
          fileSize: file.size,
        });
      }

      // Complete
      setProgress(100);
      toast.success(`Successfully uploaded ${uploadedFiles.length} file(s)`);

      // Callback
      if (onUploadComplete) {
        onUploadComplete(uploadedFiles);
      }

      // Close dialog
      handleClose();

    } catch (error) {
      console.error('Upload failed:', error);
      setError(error.message || 'Upload failed. Please try again.');
      toast.error('Upload failed: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  // Remove file from selection
  const removeFile = (index) => {
    setSelectedFiles(files => files.filter((_, i) => i !== index));
  };

  // Close dialog
  const handleClose = () => {
    if (uploading) return; // Don't close while uploading
    setDialogOpen(false);
    setSelectedFiles([]);
    setProgress(0);
    setError(null);
  };

  return (
    <>
      {/* Upload Button */}
      <Button
        variant={compact ? "outlined" : "contained"}
        startIcon={<AttachFileIcon />}
        onClick={() => fileInputRef.current?.click()}
        disabled={!entityId}
        size={compact ? "small" : "medium"}
        aria-label={compact ? "Upload files" : undefined}
        sx={{
          minWidth: compact ? 'auto' : undefined,
        }}
      >
        {compact ? "" : "Upload Files"}
      </Button>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={Object.keys(ALLOWED_FILE_TYPES).join(',')}
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      {/* Upload Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={uploading}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Upload Files</Typography>
            {!uploading && (
              <IconButton onClick={handleClose} size="small">
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        </DialogTitle>

        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* File list */}
          {selectedFiles.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="text.primary" gutterBottom>
                Selected Files ({selectedFiles.length})
              </Typography>
              <List dense>
                {selectedFiles.map((file, index) => {
                  const FileIcon = getFileIcon(file.name);
                  return (
                    <ListItem
                      key={index}
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        mb: 1,
                        bgcolor: 'background.paper',
                      }}
                    >
                      <FileIcon sx={{ mr: 2, color: 'primary.main' }} />
                      <ListItemText
                        primaryTypographyProps={{ component: 'div' }}
                        secondaryTypographyProps={{ component: 'div' }}
                        primary={
                          <Typography variant="body2" color="text.primary">
                            {file.name}
                          </Typography>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                            <Chip label={getFileTypeLabel(file.name)} size="small" />
                            <Chip label={formatFileSize(file.size)} size="small" />
                          </Box>
                        }
                      />
                      {!uploading && (
                        <ListItemSecondaryAction>
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => removeFile(index)}
                          >
                            <CloseIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      )}
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          )}

          {/* Upload progress */}
          {uploading && (
            <Box sx={{ mt: 2 }}>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                Uploading... {Math.round(progress)}%
              </Typography>
            </Box>
          )}

          {/* Info */}
          {!uploading && selectedFiles.length > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Files will be processed through the ingestion pipeline for tokenization and analysis.
            </Alert>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={uploading || selectedFiles.length === 0}
          >
            {uploading ? `Uploading...` : `Upload ${selectedFiles.length} File(s)`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FileUploadButton;
